import { Clear as ClearIcon, Done as DoneIcon, MoreHoriz } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import { Project } from '../../../../../domain/backoffice/project/Project';
import { GeoFence } from '../../../../../domain/geoFence/GeoFence';
import Text from '../../../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../../../react-components/data-table/DataTable';
import { ProjectsTableColumn } from '../../../context/tableColumns/projects';
import { BackofficeClientsProjectActionsMenu } from '../shared/BackofficeClientsProjectActionsMenu';

type ProjectsGetColumnsResponse = { [key: string]: any }[];

type ProjectsGetColumns = {
  tableColumns: Array<ProjectsTableColumn>;
  onRefreshContent: () => void;
  goToDetails: ({ target, id }: { target: `organization` | `project`; id: string }) => void;
};

export const getProjectsColumns = ({
  tableColumns,
  onRefreshContent,
  goToDetails,
}: ProjectsGetColumns): ProjectsGetColumnsResponse => {
  const columns: Array<DataTableColumnProps> = [];

  tableColumns.forEach(({ displayable, identifier, sortable }: ProjectsTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `name`:
          columns.push({
            width: 200,
            height: 45,
            sortable,
            accessorName: `name`,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.name" />,
            formatter: ({ doc: project }: { doc: Project }) => {
              const name = project.label;

              return (
                <Grid container direction="row" alignItems="center">
                  {name && name !== `` ? (
                    <RoutalTooltip title={name}>
                      <Text variant="button" onClick={(e: any) => goToDetails({ target: `project`, id: project.id })}>
                        {name.length > 35 ? `${name.substring(0, 35)}...` : `${name}`}
                      </Text>
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="button"
                      onClick={(e: any) => goToDetails({ target: `project`, id: project.id })}
                      style={{
                        fontWeight: 500,
                        color: RoutalPalette.secondary00,
                      }}
                      intlId="no_name"
                    />
                  )}
                </Grid>
              );
            },
          });
          break;
        case `company`:
          columns.push({
            width: 200,
            height: 45,
            sortable,
            accessorName: `name`,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.company" />
            ),
            formatter: ({ doc: project }: { doc: Project }) => {
              const name = project.organization.label;

              return (
                <Grid container direction="row" alignItems="center">
                  {name && name !== `` ? (
                    <RoutalTooltip title={name}>
                      <Text
                        variant="button"
                        onClick={(e: any) => goToDetails({ target: `organization`, id: project.organization.id })}
                      >
                        {name.length > 35 ? `${name.substring(0, 35)}...` : `${name}`}
                      </Text>
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="button"
                      onClick={(e: any) => goToDetails({ target: `organization`, id: project.organization.id })}
                      style={{
                        fontWeight: 500,
                        color: RoutalPalette.secondary00,
                      }}
                      intlId="no_name"
                    />
                  )}
                </Grid>
              );
            },
          });
          break;
        case `users`:
          columns.push({
            width: 100,
            sortable,
            align: `center` as AlignmentTypes,
            accessorName: `users`,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.users" />,
            formatter: ({ doc: project }: { doc: Project }) => {
              return <Text>{project.users.length}</Text>;
            },
          });
          break;
        case `max_vehicles`:
          columns.push({
            width: 60,
            sortable,
            align: `center` as AlignmentTypes,
            accessorName: `max_vehicles`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.projects.max_vehicles"
              />
            ),
            formatter: ({ doc: project }: { doc: Project }) => {
              return <Text>{project.resources.max_vehicles_plan}</Text>;
            },
          });
          break;
        case `max_clients`:
          columns.push({
            width: 60,
            sortable,
            align: `center` as AlignmentTypes,
            accessorName: `max_clients`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.projects.max_clients"
              />
            ),
            formatter: ({ doc: project }: { doc: Project }) => {
              return <Text>{project.resources.max_clients}</Text>;
            },
          });
          break;
        case `max_services_plan`:
          columns.push({
            width: 60,
            sortable,
            align: `center` as AlignmentTypes,
            accessorName: `max_services_plan`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.projects.max_services_plan"
              />
            ),
            formatter: ({ doc: project }: { doc: Project }) => {
              return <Text>{project.resources.max_stops_plan}</Text>;
            },
          });
          break;
        case `geo_fences`:
          columns.push({
            width: 60,
            sortable,
            align: `center` as AlignmentTypes,
            accessorName: `geo_fences`,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.geo_fences" />
            ),
            formatter: ({ value: geoFences }: { value: GeoFence[] }) => {
              return <Text>{geoFences.length}</Text>;
            },
          });
          break;
        case `is_managed`:
          columns.push({
            width: 80,
            sortable,
            align: `center` as AlignmentTypes,
            accessorName: `managed`,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.is_managed" />
            ),
            formatter: ({ value: managed }: { value: boolean }) => {
              return (
                <Text>
                  {managed ? (
                    <DoneIcon style={{ color: RoutalPalette.success.medium }} />
                  ) : (
                    <ClearIcon style={{ color: RoutalPalette.error.medium }} />
                  )}
                </Text>
              );
            },
          });
          break;
        case `traffic`:
          columns.push({
            width: 80,
            sortable,
            align: `center` as AlignmentTypes,
            accessorName: `traffic`,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.traffic" />
            ),
            formatter: ({ doc: project }: { doc: Project }) => {
              return (
                <Text>
                  {project.resources.traffic ? (
                    <DoneIcon style={{ color: RoutalPalette.success.medium }} />
                  ) : (
                    <ClearIcon style={{ color: RoutalPalette.error.medium }} />
                  )}
                </Text>
              );
            },
          });
          break;
        default:
          break;
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `created_at` && displayable)
      ? [
          {
            width: 120,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `created_at`,
            align: `center` as AlignmentTypes,
            sortable: true,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.projects.created_at" />
            ),
            formatter: ({ value: createdAt }) => (
              <RoutalTooltip title={dateFormat(createdAt)}>
                <Text variant="body">{pastDateTime(createdAt)}</Text>
              </RoutalTooltip>
            ),
          } as DataTableColumnProps,
        ]
      : []),
    {
      column: `actions`,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.actions" />,
      align: `center`,
      width: 120,
      columnStyle: {
        width: `120px`,
      },
      valueCellStyle: {
        width: `100%`,
      },
      formatter: ({ doc: project }: { doc: Project }) => (
        <Grid container direction="row" wrap="nowrap" justifyContent="center">
          <BackofficeClientsProjectActionsMenu
            project={project}
            type="popover"
            popoverProps={{
              hideArrow: true,
              content: <MoreHoriz />,
              containerStyle: {
                width: `unset`,
              },
              style: { height: `18px`, color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
            }}
            refreshAfterUpdate={() => onRefreshContent()}
          />
        </Grid>
      ),
    },
  ] as Array<DataTableColumnProps>;
};
